import type { InvitedBy } from '@clareefai/types';
import {
  AlertTriangle,
  Building,
  Calendar,
  CheckCircle,
  Mail,
  Pencil,
  Phone,
  RefreshCw,
  Trash2,
  User,
  UserCog,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import { FirstLoginChip, InvitedByLine } from '../../shared/ProfileMeta';
import type { ProspectView, SalespersonView } from './types';

interface ProspectHeroProps {
  prospect: ProspectView;
  hasLoggedIn?: boolean;
  invitedBy?: InvitedBy | null;
  salesperson: SalespersonView | null;
  canEdit: boolean;
  canDelete: boolean;
  isResendingInvite: boolean;
  onEdit: () => void;
  onResendInvite: () => void;
  onDelete: () => void;
  onAssignSalesperson: () => void;
}

const ProspectHero: React.FC<ProspectHeroProps> = ({
  prospect,
  hasLoggedIn,
  invitedBy,
  salesperson,
  canEdit,
  canDelete,
  isResendingInvite,
  onEdit,
  onResendInvite,
  onDelete,
  onAssignSalesperson,
}) => {
  const { t } = useTranslation();

  return (
    <div className="feel-in relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#4339A0] via-[#5a4dba] to-[#3d2f8f] text-white shadow-lg shadow-[#4339A0]/20">
      <div className="p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex items-start gap-5">
            <div className="relative shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/10 backdrop-blur-sm ring-4 ring-white/30 rounded-2xl flex items-center justify-center shadow-lg">
                <User className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </div>
              <div className="absolute -bottom-1.5 -right-1.5 bg-yellow-400 text-yellow-900 p-1.5 rounded-lg shadow-md">
                <CheckCircle className="w-5 h-5" />
              </div>
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-balance">{prospect.name}</h1>
              {prospect.company && <p className="text-base text-[#cfc8f5] mt-1">{prospect.company}</p>}

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                  {prospect.status.replace(/_/g, ' ').toUpperCase()}
                </span>
                <FirstLoginChip loggedIn={hasLoggedIn} />
                {prospect.sectors.map((sector) => (
                  <span key={sector} className="px-3 py-1 bg-[#4339A0]/40 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                    {sector}
                  </span>
                ))}
              </div>

              {invitedBy && <InvitedByLine invitedBy={invitedBy} className="text-white mt-4" />}

              <div className="flex items-center gap-1 mt-4">
                <a
                  href={`mailto:${prospect.email}`}
                  aria-label={t('common.email', { defaultValue: 'Email' })}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-[#cfc8f5] hover:text-white hover:bg-white/10 active:scale-[0.92] transition-[color,background-color,transform] duration-150"
                >
                  <Mail className="w-5 h-5" />
                </a>
                {prospect.phone && (
                  <a
                    href={`tel:${prospect.phone}`}
                    aria-label={t('common.phone', { defaultValue: 'Phone' })}
                    className="flex items-center justify-center w-9 h-9 rounded-lg text-[#cfc8f5] hover:text-white hover:bg-white/10 active:scale-[0.92] transition-[color,background-color,transform] duration-150"
                  >
                    <Phone className="w-5 h-5" />
                  </a>
                )}
              </div>

              {(canEdit || canDelete) && (
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {canEdit && (
                    <button
                      onClick={onEdit}
                      className="flex items-center gap-1.5 pl-3 pr-3.5 py-2 bg-white/15 hover:bg-white/25 active:scale-[0.96] text-white text-sm font-medium rounded-lg transition-[background-color,transform] duration-150"
                    >
                      <Pencil className="w-4 h-4" />
                      <span>{t('common.edit')}</span>
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={onResendInvite}
                      disabled={isResendingInvite}
                      className="flex items-center gap-1.5 pl-3 pr-3.5 py-2 bg-white/15 hover:bg-white/25 active:scale-[0.96] disabled:opacity-60 disabled:active:scale-100 text-white text-sm font-medium rounded-lg transition-[background-color,transform] duration-150"
                    >
                      <RefreshCw className={`w-4 h-4 ${isResendingInvite ? 'animate-spin' : ''}`} />
                      <span>{isResendingInvite ? t('common.sending') : t('prospects.sendMagicLink')}</span>
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={onDelete}
                      className="flex items-center gap-1.5 pl-3 pr-3.5 py-2 bg-red-500/80 hover:bg-red-600 active:scale-[0.96] text-white text-sm font-medium rounded-lg transition-[background-color,transform] duration-150"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>{t('common.delete')}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3 lg:w-[360px] shrink-0">
            <div
              onClick={onAssignSalesperson}
              className="rounded-xl bg-white/10 backdrop-blur-sm p-4 ring-1 ring-inset ring-white/15 hover:bg-white/15 active:scale-[0.99] transition-[background-color,transform] duration-150 cursor-pointer group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-[#b8acec]" />
                  <div className="text-sm text-[#b8acec]">{t('prospects.assignedSalesperson')}</div>
                </div>
                <span className="text-[#b8acec] opacity-0 group-hover:opacity-100 transition-opacity">
                  <Pencil className="w-4 h-4" />
                </span>
              </div>
              {salesperson ? (
                <div className="flex items-center gap-3">
                  <Avatar
                    src={salesperson.avatar.length > 2 ? salesperson.avatar : undefined}
                    name={salesperson.name}
                    className="w-10 h-10 shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="text-base font-semibold truncate">{salesperson.name}</div>
                    <div className="text-sm text-[#b8acec] truncate">{salesperson.role}</div>
                  </div>
                </div>
              ) : (
                <div className="text-base font-semibold text-yellow-300 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 shrink-0" />
                  <span>{t('prospects.clickToAssign')}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-white/10 backdrop-blur-sm p-4 ring-1 ring-inset ring-white/15">
                <Building className="w-5 h-5 text-[#b8acec] mb-2" />
                <div className="text-xs text-[#b8acec] mb-0.5">{t('prospects.companySize')}</div>
                <div className="text-base font-semibold tabular-nums">{prospect.companySize || 'N/A'}</div>
              </div>

              <div className="rounded-xl bg-white/10 backdrop-blur-sm p-4 ring-1 ring-inset ring-white/15">
                <Calendar className="w-5 h-5 text-[#b8acec] mb-2" />
                <div className="text-xs text-[#b8acec] mb-0.5">{t('prospects.created')}</div>
                <div className="text-base font-semibold tabular-nums">
                  {new Date(prospect.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectHero;
